import { h, Component } from 'preact';
import Store from 'store/store';
import { joinClasses, sortObjectByOrder } from 'utils/utils';
import { TakeAgain } from 'components/bolt/icons/quizPhotoboothIcons';
import Close from 'components/bolt/icons/close';
import Check from 'components/bolt/icons/check';
import HTML from 'components/core/html/html';
import s from 'components/bolt/quizPhotobooth/quiz.sss';

export default class Quiz extends Component {
	constructor(props) {
		super(props);
		this.goBack = this.goBack.bind(this);
		this.goToPrevQuestion = this.goToPrevQuestion.bind(this);
		this.getResult = this.getResult.bind(this);
		this.recordAnswer = this.recordAnswer.bind(this);
		this.resetQuiz = this.resetQuiz.bind(this);
		this.resultPageButtonClicked = this.resultPageButtonClicked.bind(this);
		this.renderQuestion = this.renderQuestion.bind(this);
		this.setStateAfterAnimation = this.setStateAfterAnimation.bind(this);

		this.questions = sortObjectByOrder(props?.quiz?.questions);
		this.state = {
			activeQuestionAssetURL: '',
			questionResults: {},
			lastOptionSelected: null,
			isRevealing: false,
			showResultPage: false,
			quizResultData: null,
			savedQuestionIndex: 0,
		};
		if (!this.questions.length) {
			this.setResult(this.state.questionResults, true);
		}
	}

	get appData() {
		return this.props.appData || Store.get().appData;
	}
	get fontStyles() {
		return this.appData?.styling?.fontVariables || {};
	}
	get copy() {
		return this.appData?.copy?.baseCopy || {};
	}

	componentDidMount() {
		Store.on('QUIZPHOTOBOOTH_QUIZ_RESET', this.resetQuiz);
		this.checkResult();
		this.reveal();
	}

	componentDidUpdate(prevProps) {
		if (this.props.quizResult !== prevProps.quizResult) {
			this.checkResult();
		}
	}

	componentWillUnmount() {
		Store.off('QUIZPHOTOBOOTH_QUIZ_RESET', this.resetQuiz);
		this.revealEndTimer && this.props.revealAnimData && clearTimeout(this.revealEndTimer);
		this.revealAnim?.destroy();
	}

	async reveal() {
		const { revealAnimData, isDesktopWidth } = this.props;
		if (!revealAnimData || revealAnimData.type !== 'animation') {
			return;
		}
		const { revealAnimDesktop, revealAnimMobile } = revealAnimData;
		const lottie = await import('lottie-web');
		this.revealAnim = lottie.loadAnimation({
			container: this.$revealAnimContainer,
			renderer: 'svg',
			loop: false,
			autoplay: false,
			path: isDesktopWidth ? revealAnimDesktop : revealAnimMobile
		});
	}

	checkResult(quizResult = this.props.quizResult) {
		if (!quizResult) {
			return;
		}
		const quizResultData = this.props.results?.[quizResult] || {};
		this.setState({ quizResultData });
		const revealAnimData = this.props.revealAnimData;
		if (revealAnimData) {
			if (revealAnimData.type === 'animation') this.revealAnim.play();
			this.setState({ isRevealing: true, quizResultData: quizResultData });
			this.revealEndTimer = setTimeout(() => {
				this.showResults(quizResultData);
				this.setState({ isRevealing: false });
			}, +revealAnimData.revealAnimLength);
		} else {
			this.showResults(quizResultData);
		}
	}

	setStateAfterAnimation(newState) {
		const animationTime = 1000;
		setTimeout(() => {
			this.setState(newState);
		}, animationTime);
	}

	showResults(resultsData = this.state.quizResultData) {
		if (resultsData?.resultPage) {
			this.setState({ showResultPage: true });
			Store.get().quizPhotobooth.set({ showResultPage: true });
			Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', 'results');
		} else {
			this.props.advanceStep();
		}
		this.props.endRevealAnim();
	}

	goToPrevQuestion() {
		const activeQuestionIndex = Store.get().quizPhotobooth.question;
		if (activeQuestionIndex === 0) return;
		Store.get().quizPhotobooth.set({ question: activeQuestionIndex - 1 });
		this.setStateAfterAnimation({ savedQuestionIndex: this.state.savedQuestionIndex - 1, lastOptionSelected: this.state.questionResults[activeQuestionIndex - 1].index });
	}

	goToNextQuestion() {
		const activeQuestionIndex = Store.get().quizPhotobooth.question;

		if (this.state.questionResults?.[activeQuestionIndex + 1] !== undefined) {
			this.setStateAfterAnimation({ savedQuestionIndex: this.state.savedQuestionIndex + 1, lastOptionSelected: this.state.questionResults[activeQuestionIndex + 1].index });
		} else {
			this.setStateAfterAnimation({ savedQuestionIndex: this.state.savedQuestionIndex + 1, lastOptionSelected: null });
		}
		if (activeQuestionIndex < this.questions.length - 1) {
			Store.get().quizPhotobooth.set({ question: activeQuestionIndex + 1 });
		} else {
			return this.setResult(this.state.questionResults);
		}
	}

	getResult(questionResults = this.state.questionResults) {
		const tally = Object.values(questionResults || {}).reduce((total, result) => {
			total[result.resultWeighting] = (total[result.resultWeighting] || 0) + 1;
			return total;
		}, {});
		if ('correct' in tally || 'incorrect' in tally) {
			const correctAnswers = tally.correct || 0;
			this.setState({ correctAnswers });
			// sort results in descending order by 'correctAnswersRequired' property
			const orderedResults = sortObjectByOrder(this.props.results, (v, id) => ({ ...v, id }), 'correctAnswersRequired').reverse();
			// return the first result where correctAnswersRequired is greater than or equal to the number of correct answers
			return orderedResults.find(e => e.correctAnswersRequired <= correctAnswers)?.id;
		}
		const values = Object.values(tally);
		const topScore = Math.max(...values);
		const topResults = Object.keys(tally).filter(key => tally[key] === topScore);
		return topResults[Math.floor(Math.random() * topResults.length)];
	}

	setResult(questionResults = this.state.questionResults, skip = false) {
		const result = this.getResult(questionResults);
		this.props.setResult(result);
		setTimeout(() => {
			Store.get().quizPhotobooth.set({ question: 0 });
		}, 1000);
		if (!skip) {
			Store.emit('TRACK_QUIZPHOTOBOOTH_RESULT_REVEAL', result);
		}
	}

	recordAnswer(optionIndex, option) {
		const activeQuestionIndex = Store.get().quizPhotobooth.question;
		const { questionResults } = this.state;
		const result = {
			index: optionIndex,
			question: activeQuestionIndex + 1,
			resultWeighting: option.resultWeighting
		};
		const newQuestionResults = { ...questionResults, [activeQuestionIndex]: result };
		this.setState({ questionResults: newQuestionResults });
	}

	onOptionSelect(option, optionIndex) {
		const activeQuestionIndex = Store.get().quizPhotobooth.question;
		Store.emit('TRACK_QUIZPHOTOBOOTH_ANSWER_CLICK', { questionNum: activeQuestionIndex + 1, answer: option.copy });
		this.setState({ lastOptionSelected: optionIndex });
		this.recordAnswer(optionIndex, option);
		if (option.resultWeighting !== 'correct' && option.resultWeighting !== 'incorrect') {
			this.goToNextQuestion();
		}
	}

	resetQuiz() {
		this.setState({
			questionResults: {},
			revealAnimEnded: false,
			lastOptionSelected: null,
			showResultPage: false,
			// quizResultData: null,
		});
		Store.get().quizPhotobooth.set({ question: 0, showResultPage: false });
	}

	goBack() {
		const activeQuestionIndex = Store.get().quizPhotobooth.question;
		if (activeQuestionIndex === 0) {
			this.props.backStep();
			return;
		}
		this.goToPrevQuestion();
	}

	renderBackButton() {
		return (
			<button class={s.backContainer} onClick={this.goBack}>
				<div class={s.backArrow} />
				<HTML class={this.fontStyles.quizPhotoboothInfo} content={this.copy.$QUIZPHOTOBOOTH_BACK} markdown />
			</button>
		);
	}

	// when an option's resultWeighting is 'correct' or 'incorrect', show a badge under certain conditions:
	// always show the correct answer whether it is chosen or not
	// if the option was selected and is incorrect, show it
	renderBadge(option, index, i) {
		const isCorrectOrIncorrect = option.resultWeighting === 'correct' || option.resultWeighting === 'incorrect';
		if (!isCorrectOrIncorrect) return;
		const wasSelected = this.state.lastOptionSelected === i;
		const questionActive = this.state.savedQuestionIndex === index;
		const wasSelectedAndIncorrect = questionActive && wasSelected && option.resultWeighting === 'incorrect';
		const wasntSelectedAndCorrect = questionActive && typeof this.state.lastOptionSelected === 'number' && option.resultWeighting === 'correct';
		const visible = !this.props.quiz?.noImmediateResultReveal && (wasSelectedAndIncorrect || wasntSelectedAndCorrect);
		return (
			<div class={joinClasses(s.badge, visible && s.visible, option.resultWeighting === 'incorrect' && s.incorrect)}>
				{option.resultWeighting === 'correct' && <Check />}
				{option.resultWeighting === 'incorrect' && <Close />}
			</div>
		);
	}

	renderOption(option, index, i, data, showOverlays) {
		const { optionsGrid } = data;
		let selected = this.state.lastOptionSelected === i && this.state.savedQuestionIndex === index;
		const answered = this.state.questionResults[index]?.index === i;
		selected = selected || answered;

		return (
			<div class={s.optionContainer}>
				{this.renderBadge(option, index, i)}
				<button
					class={joinClasses(
						s.option,
						optionsGrid ? s.grid : s.flex,
						selected && s.selected,
						!this.props.quiz?.noImmediateResultReveal && showOverlays && !selected && s.disabled,
					)}
					key={`q${index}opt${i}`}
					onClick={() => {
						this.onOptionSelect(option, i);
					}}
				>
					<span class={this.fontStyles.quizPhotoboothOption}>{option.copy}</span>
				</button>
			</div>
		);
	}

	// when an option with a resultWeighting of 'correct' or 'incorrect' is selected, show an overlay over the question hero image
	renderOverlay(data, showOverlays, asset, options) {
		const copy = this.copy;
		const correct = options[this.state.lastOptionSelected]?.resultWeighting === 'correct';
		return (
			<div class={joinClasses(s.assetOverlay, showOverlays && s.visible, asset && s.asset)}>
				<div class={s.assetOverlayInner}>
					<div class={joinClasses(s.correctIcon, !correct && s.incorrect)}>
						{correct ? <Check /> : <Close />}
					</div>
					<div class={joinClasses(this.fontStyles.quizPhotoboothQuestion, s.correctHeader)}>
						{
							correct ? (data.correctTextHeading || copy.$QUIZPHOTOBOOTH_CORRECT || 'Correct!') : (data.incorrectTextHeading || copy.$QUIZPHOTOBOOTH_INCORRECT || 'Incorrect!')
						}
					</div>
					<HTML class={joinClasses(s.assetText, this.fontStyles.quizPhotoboothCopy)} content={correct ? data.correctText : data.incorrectText} markdown />
				</div>
			</div>
		);
	}

	nextQuestionButtonClick(index) {
		const option = Object.values(this.questions[index].options)[this.state.lastOptionSelected];
		this.recordAnswer(this.state.lastOptionSelected, option);
		this.goToNextQuestion();
		this.setState({ savedQuestionIndex: index });
		this.setStateAfterAnimation({ savedQuestionIndex: this.state.savedQuestionIndex + 1 });
	}

	// when an option with a resultWeighting of 'correct' or 'incorrect' is selected, show a 'next question' button
	// and do not move on to the next question until that button is clicked
	renderNextQuestionButton(index, showOverlays) {
		return (
			<button
				class={joinClasses(s.option, s.selected, s.nextQuestion, showOverlays && s.visible, this.fontStyles.quizPhotoboothOption)}
				onClick={() => {
					this.nextQuestionButtonClick(index);
				}}
			>
				{this.copy.$QUIZPHOTOBOOTH_NEXT_QUESTION || 'Next Question'}
			</button>
		);
	}

	renderQuestion(data, index) {
		const { quiz, quizResult } = this.props;
		const activeQuestionIndex = Store.get().quizPhotobooth.question;
		const { isRevealing, showResultPage, questionResults } = this.state;
		const isActive = index === activeQuestionIndex;
		const { asset, assetAltText, question, optionsGrid } = data;
		const options = sortObjectByOrder(data.options);
		const fontStyles = this.fontStyles;

		const questionBackgroundImage = this.props.isDesktopWidth ? data.backgroundImageDesktop : data.backgroundImageMobile;
		const questionBackgroundImageIsVideo = questionBackgroundImage?.endsWith('.mp4') || questionBackgroundImage?.endsWith('.webm');
		const questionBackgroundStyle = !questionBackgroundImageIsVideo && questionBackgroundImage ? { backgroundImage: `url(${questionBackgroundImage})` } : {};

		const assetIsVideo = asset?.endsWith('.mp4') || asset?.endsWith('.webm');

		// for pass/fail questions, show overlays and 'next question' button once option is selected
		const isSuccessFail = options.some(opt => opt.resultWeighting === 'correct' || opt.resultWeighting === 'incorrect');
		let showOverlays = isSuccessFail && typeof this.state.lastOptionSelected === 'number' && this.state.savedQuestionIndex === index;

		const isAnswered = showOverlays && questionResults[index] !== undefined;

		return (
			<div
				class={joinClasses(
					s.questionContainer,
					isActive && !showResultPage && s.active,
					isRevealing && s.revealing
				)}
				style={questionBackgroundStyle}
			>
				{!!questionBackgroundImage && questionBackgroundImageIsVideo && <video class={s.video} src={questionBackgroundImage} autoplay loop muted playsinline />}
				{this.props.quiz.hasProgressBar && this.renderProgressBar(quizResult)}
				<div class={joinClasses(s.assetContainer, assetIsVideo && s.assetVideo)}>
					{!!asset && !assetIsVideo ? (
						<img class={joinClasses(s.asset, !quiz?.noImmediateResultReveal && showOverlays && s.blur)} src={asset} alt={assetAltText} />
					) : (
						<video class={joinClasses(s.video, s.asset)} src={asset} autoplay loop muted playsinline />
					)}
					{!quiz?.noImmediateResultReveal && this.renderOverlay(data, showOverlays, asset, options)}
				</div>
				<HTML class={joinClasses(s.questionCopy, fontStyles.quizPhotoboothQuestion)} content={question} markdown />
				<div class={joinClasses(optionsGrid ? s.optionsGrid : s.optionsFlex, this.state.savedQuestionIndex != activeQuestionIndex && s.transitioning, isAnswered && !showResultPage && s.answered )}>
					{options.map((opt, i) => this.renderOption(opt, index, i, data, showOverlays))}
				</div>
				{this.renderNextQuestionButton(index, showOverlays)}
			</div>
		);
	}

	resultPageButtonClicked() {
		Store.get().quizPhotobooth.set({ showResultPage: false });
		Store.emit('TRACK_QUIZPHOTOBOOTH_RESULT_SHARE');
		this.props.advanceStep();
	}

	renderResultNextStepButton() {
		return (
			<button class={joinClasses(s.longBtn, s.variableWidth)} onClick={this.resultPageButtonClicked} key="next-step">
				<HTML class={joinClasses(this.fontStyles.quizPhotoboothButton)} content={this.copy.$QUIZPHOTOBOOTH_REVEAL_BUTTON_CTA} markdown />
			</button>
		);
	}

	renderResultPage(data) {
		const isDesktopWidth = this.props.isDesktopWidth;

		const fontStyles = this.fontStyles;
		const copy = this.copy;

		const backgroundImage = isDesktopWidth ? data?.backgroundDesktop : data?.backgroundMobile;
		const isBackgroundImageVideo = backgroundImage?.endsWith('.mp4') || backgroundImage?.endsWith('.webm');
		const resultBackgroundStyle = !isBackgroundImageVideo && backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : {};
		const heroImage = isDesktopWidth ? data?.heroImageDesktop : data?.heroImageMobile || data?.heroImageDesktop;
		const isHeroImageVideo = heroImage?.endsWith('.mp4') || heroImage?.endsWith('.webm');
		const userName = this.props.inputs?.name;

		return (
			<div class={joinClasses(s.resultContainer, this.state.showResultPage && s.show)} style={resultBackgroundStyle}>
				{!!backgroundImage && isBackgroundImageVideo && <video class={s.video} src={backgroundImage} autoplay loop muted playsinline />}
				{!!heroImage && isHeroImageVideo ? (
					<video class={joinClasses(s.video, s.resultHeroImage)} src={heroImage} autoplay loop muted playsinline />
				) : (
					<img src={heroImage} class={s.resultHeroImage} alt={data?.heroImageAltText} key="hero" />
				)}
				{/* {!!heroImage && <img src={heroImage} class={s.resultHeroImage} alt={data?.heroImageAltText} key="hero" />} */}
				{data?.showScore && (
					<div class={joinClasses(s.resultName, fontStyles.quizPhotoboothOption)}>
						{this.state.correctAnswers}/{this.questions.length} {copy.$QUIZPHOTOBOOTH_SCORE_LABEL || 'correct'}
					</div>
				)}
				{!!userName && <div class={joinClasses(s.resultName, isDesktopWidth ? fontStyles.quizPhotoboothTitle : fontStyles.quizPhotoboothHeader)} key="name">{userName}</div>}
				<HTML class={joinClasses(s.resultTitle, isDesktopWidth ? fontStyles.quizPhotoboothTitle : fontStyles.quizPhotoboothHeader)} content={data?.titleText} markdown key="title" />
				<HTML class={joinClasses(s.resultDescription, isDesktopWidth ? fontStyles.quizPhotoboothCopy : 'medium')} content={data?.descriptionText} markdown key="description" />
				{this.renderResultNextStepButton()}
				<button class={joinClasses(s.takeAgain, fontStyles.quizPhotoboothOption)} onClick={this.props.backToIntro} key="retry">
					<TakeAgain />
					<HTML tag="span" content={copy.$QUIZPHOTOBOOTH_REVEAL_BUTTON_REDO} markdown />
				</button>
			</div>
		);
	}

	renderProgressBar(quizResult) {
		const activeQuestionIndex = Store.get().quizPhotobooth.question;
		const numQuestions = this.questions.length;
		const progress = (activeQuestionIndex + 1) / numQuestions * 100;
		const questionLabel = this.copy.$QUIZPHOTOBOOTH_QUESTION_LABEL || '';
		const labelBefore = this.props.quiz.progressBarLabelBeforeQuestionNumber;
		return (
			<div class={joinClasses(s.progressBarWrapper, quizResult && s.done)}>
				{this.renderBackButton()}
				<div class={joinClasses(s.progressBarLabel, this.fontStyles.quizPhotoboothCopy)}>
					{labelBefore && questionLabel + ' '}
					{activeQuestionIndex + 1}
					/
					{numQuestions}
					{!labelBefore && ' ' + questionLabel}
				</div>
				<div class={s.progressBarContainer}>
					<div class={s.progressBarEmpty} />
					<div class={s.progressBarFull} style={{ width: `${progress}%` }} />
				</div>
			</div>
		);
	}

	render({ transition, quiz, results, quizResult, assets, isDesktopWidth }, { isRevealing, quizResultData }) {
		const quizBackgroundImage = isDesktopWidth ? quiz.quizBackgroundDesktop : quiz.quizBackgroundMobile;
		const isVideo = quizBackgroundImage?.endsWith('.mp4') || quizBackgroundImage?.endsWith('.webm');
		const quizBackgroundStyle = !isVideo && quizBackgroundImage ? { backgroundImage: `url(${quizBackgroundImage})` } : {};
		const revealAnim = assets?.revealAnim;

		// Use first result as placeholder if quiz not finished yet
		const resultData = quizResultData?.resultPage || Object.values(results || {})[0];

		return (
			<div class={joinClasses(s.container, s[`transition-${transition}`])} style={quizBackgroundStyle}>
				{!!quizBackgroundImage && isVideo && <video class={joinClasses(s.video, this.state.showResultPage && s.resultsVideo)} src={quizBackgroundImage} autoplay loop muted playsinline />}
				{this.questions?.map(this.renderQuestion)}
				{revealAnim?.type === 'video' ? (
					<video
						class={joinClasses(s.revealAnimContainer, quizResult && isRevealing && s.show)}
						ref={$ref => this.$revealAnimContainer = $ref}
						src={isDesktopWidth ? revealAnim.revealAnimDesktop : revealAnim.revealAnimMobile}
						autoplay
						muted
						loop
					/>
				) : revealAnim?.type === 'animation' ? (
					<div
						class={joinClasses(s.revealAnimContainer, quizResult && s.show)}
						ref={$ref => this.$revealAnimContainer = $ref}
					/>
				) : (
					<div class={joinClasses(s.loadingContainer, isRevealing && s.show)}>
						<img src={isDesktopWidth ? revealAnim?.revealAnimDesktop : revealAnim?.revealAnimMobile} class={s.loadingImage} />
						<div class={joinClasses(s.loadingBar, quizResult && s.loading)} />
						<p class={joinClasses(s.loadingText, this.fontStyles.quizPhotoboothMessage)}>
							{this.copy.$QUIZPHOTOBOOTH_PHOTOBOOTH_LOADING}
						</p>
					</div>
				)}
				{this.renderResultPage(resultData)}
			</div>
		);
	}
}